import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, DollarSign, Calendar, Award } from "lucide-react";
import { useReportingData } from "@/hooks/useReportingData";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface StatsCardsProps {
  dateRange?: { from: Date; to: Date };
}

export function StatsCards({ dateRange }: StatsCardsProps) {
  const { revenueData, attendanceData, enrollmentData } = useReportingData(dateRange);

  // Total Revenue
  const totalRevenue = useMemo(() => revenueData.reduce((sum, d) => sum + (d.revenue || 0), 0), [revenueData]);
  const revenueChange = useMemo(() => {
    if (revenueData.length < 2) return 0;
    const last = revenueData[revenueData.length - 1]?.revenue || 0;
    const prev = revenueData[revenueData.length - 2]?.revenue || 0;
    if (prev === 0) return last > 0 ? 100 : 0;
    return ((last - prev) / prev) * 100;
  }, [revenueData]);

  // Active Students
  const { data: activeStudents } = useQuery({
    queryKey: ["activeStudents", dateRange],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("enrollments")
        .select("student_id")
        .eq("status", "active");
      if (error) throw error;
      // Unique student IDs
      return Array.from(new Set((data || []).map((e) => e.student_id))).length;
    },
  });

  // Attendance Rate
  const avgAttendance = useMemo(() => {
    if (!attendanceData.length) return 0;
    const total = attendanceData.reduce((sum, d) => sum + (d.attendance || 0), 0);
    return Math.round(total / attendanceData.length);
  }, [attendanceData]);
  const attendanceChange = useMemo(() => {
    if (attendanceData.length < 2) return 0;
    const last = attendanceData[attendanceData.length - 1]?.attendance || 0;
    const prev = attendanceData[attendanceData.length - 2]?.attendance || 0;
    if (prev === 0) return last > 0 ? 100 : 0;
    return last - prev;
  }, [attendanceData]);

  // Retention Rate (simple: active students this period / last period)
  const retentionRate = useMemo(() => {
    if (enrollmentData.length < 2) return 0;
    const last = enrollmentData[enrollmentData.length - 1]?.newStudents || 0;
    const prev = enrollmentData[enrollmentData.length - 2]?.newStudents || 0;
    if (prev === 0) return last > 0 ? 100 : 0;
    return Math.round((last / prev) * 100);
  }, [enrollmentData]);
  const retentionChange = useMemo(() => {
    if (enrollmentData.length < 2) return 0;
    const last = enrollmentData[enrollmentData.length - 1]?.newStudents || 0;
    const prev = enrollmentData[enrollmentData.length - 2]?.newStudents || 0;
    if (prev === 0) return last > 0 ? 100 : 0;
    return last - prev;
  }, [enrollmentData]);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card className="shadow border-0 bg-gradient-to-br from-white via-blue-50 to-blue-100/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
          <DollarSign className="h-4 w-4 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-900">${totalRevenue.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {revenueChange > 0 ? "+" : ""}{revenueChange.toFixed(1)}% from last period
          </p>
        </CardContent>
      </Card>
      <Card className="shadow border-0 bg-gradient-to-br from-white via-green-50 to-green-100/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Students</CardTitle>
          <Users className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-900">{activeStudents ?? "-"}</div>
          <p className="text-xs text-muted-foreground">
            {/* Could add change from last period if desired */}
          </p>
        </CardContent>
      </Card>
      <Card className="shadow border-0 bg-gradient-to-br from-white via-purple-50 to-purple-100/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Class Attendance</CardTitle>
          <Calendar className="h-4 w-4 text-purple-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-900">{avgAttendance}%</div>
          <p className="text-xs text-muted-foreground">
            {attendanceChange > 0 ? "+" : ""}{attendanceChange.toFixed(1)}% from last period
          </p>
        </CardContent>
      </Card>
      <Card className="shadow border-0 bg-gradient-to-br from-white via-yellow-50 to-yellow-100/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Retention Rate</CardTitle>
          <Award className="h-4 w-4 text-yellow-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-yellow-900">{retentionRate}%</div>
          <p className="text-xs text-muted-foreground">
            {retentionChange > 0 ? "+" : ""}{retentionChange.toFixed(1)}% from last period
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
